import {
  CloseModalButton,
  FormStep,
  FormWrapper,
  Header,
  InnerScroller,
  MobileHeading,
  Modal,
  PrevStepButton,
} from '@common/components/BookingForms';
import { Spinner } from '@common/components/Spinner';
import { SignInModalForm } from '@features/SignIn';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { PriceWithPrefixAndSuffix } from 'shared-types';
import { Guest } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import { SpaClassTimeSlot } from '../SpaClasses.types';
import { spaClassFormSchema, SpaClassFormValues } from '../SpaClassForm.schema';
import { SpaClassesGuestDetailsStep } from './SpaClassesGuestDetailsStep';

type SelectedTimeslot = SpaClassTimeSlot & {
  discount?: { discountedPrice: PriceWithPrefixAndSuffix } | null;
};

type SpaClassesConfigurationWizardProps = {
  selectedTimeslot: SelectedTimeslot;
  closeConfiguration: () => void;
  handleBasketUpdate: (data: SpaClassFormValues) => Promise<void>;
  isFetching: boolean;
  existingGuests: Guest[];
  property: Property;
  signIn: (...args: any[]) => any;
  showGenderNeutral: boolean;
};

export const SpaClassesConfigurationWizard: FC<SpaClassesConfigurationWizardProps> = ({
  selectedTimeslot,
  closeConfiguration,
  handleBasketUpdate,
  isFetching,
  existingGuests,
  property,
  signIn,
  showGenderNeutral,
}) => {
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [loginError, setLoginError] = useState('');

  const { numberOfSpacesAvailable, gender, spaClass, price, discount, maxBookingsPerClient } =
    selectedTimeslot;

  const form = useForm<SpaClassFormValues>({
    resolver: zodResolver(spaClassFormSchema),
    defaultValues: {
      guests: [{ gender: gender || '' }],
    },
  });

  const handleSignInModalClose = () => {
    setIsSignInModalOpen(false);
  };

  const handleSignIn = async (data: any) => {
    try {
      await signIn(property.id, data);
      setIsSignInModalOpen(false);
    } catch (e) {
      setLoginError('invalidLogin');
    }
  };

  const finishWizardAction = async (data: SpaClassFormValues) => {
    await handleBasketUpdate(data);
    closeConfiguration();
  };

  const totalGuestAmount = maxBookingsPerClient ?? numberOfSpacesAvailable;

  return (
    <FormProvider {...form}>
      <SignInModalForm
        isModalOpen={isSignInModalOpen}
        handleModalClose={handleSignInModalClose}
        onSubmit={handleSignIn}
        loginError={loginError}
      />
      <Modal isVisible>
        <Spinner isActive={isFetching} showDelayMessage />
        <Header>
          <PrevStepButton onClick={closeConfiguration} />
          <MobileHeading>{spaClass.name}</MobileHeading>
          <CloseModalButton onClick={closeConfiguration} />
        </Header>
        <InnerScroller>
          <FormWrapper>
            <FormStep isNarrow noGutter>
              <SpaClassesGuestDetailsStep
                existingGuests={existingGuests}
                totalGuestAmount={totalGuestAmount}
                isGenderSpecific={gender}
                spaClass={spaClass}
                timeSlotPrice={price}
                discount={discount}
                showGenderNeutral={showGenderNeutral}
                onSubmit={finishWizardAction}
              />
            </FormStep>
          </FormWrapper>
        </InnerScroller>
      </Modal>
    </FormProvider>
  );
};
